<!--
 * @Description: 平台商户余额统计（复用充值列表 page=0 的「当前汇总」行，不改服务端）
-->
<template>
  <div class="merchant-pay">
    <div class="merchant-pay__toolbar">
      <el-button type="primary" size="small" :loading="loadingStatus" @click="loadBalanceStats">
        刷新
      </el-button>
      <el-button size="small" :disabled="!tableData.length" @click="exportExcel">
        导出Excel
      </el-button>
    </div>

    <el-alert
      v-if="negativeCount"
      class="merchant-pay__alert"
      type="error"
      show-icon
      :closable="false"
      :title="`有 ${negativeCount} 个商户剩余金额为负（红色行 + 欠费标签），请尽快处理`"
    />

    <Table
      :list-query-params.sync="listQueryParams"
      v-bind="tableAttrs"
      v-on="tableEvent"
    />
  </div>
</template>

<script>
import Table from "@/components/Table/index.vue";
import XLSX from "xlsx";
import { getPathName } from "@/utils/index.js";
import { platformMerchantRechargeList } from "@/api/business";
import {
  SUMMARY_REMARK,
  fetchActiveMerchants,
  checkPlatformUser,
} from "./utils";

const EXPORT_HEADERS = [
  "商户名称",
  "商户ID",
  "总支付(元)",
  "商户应付(元)",
  "剩余(元)",
  "统计时间",
];
const EXPORT_KEYS = [
  "merchantName",
  "merchantId",
  "total_amount",
  "total_merchant_amount",
  "remain_amount",
  "create_time",
];

export default {
  name: "platformMerchantBalanceStats",
  components: {
    Table,
  },
  beforeRouteEnter(to, from, next) {
    if (checkPlatformUser()) {
      next();
    } else {
      next("/404");
    }
  },
  data() {
    return {
      merchantOptions: [],
      tableData: [],
      listQueryParams: {
        pageNum: 1,
        pageSize: 20,
        total: 0,
      },
      tableConfig: [
        { label: "商户名称", width: "180", value: "merchantName" },
        { label: "商户ID", width: "100", value: "merchantId" },
        { label: "总支付(元)", width: "120", value: "total_amount" },
        { label: "商户应付(元)", width: "120", value: "total_merchant_amount" },
        { label: "剩余(元)", width: "140", value: "remain_amount", format: "dangerMoney" },
        { label: "统计时间", width: "170", value: "create_time" },
      ],
      loadingStatus: false,
    };
  },
  computed: {
    negativeCount() {
      return this.tableData.filter((row) => row.isNegativeRemain).length;
    },
    pagedTableData() {
      const { pageNum, pageSize } = this.listQueryParams;
      const start = (pageNum - 1) * pageSize;
      return this.tableData.slice(start, start + pageSize);
    },
    tableAttrs() {
      return {
        config: this.tableConfig,
        tableData: this.pagedTableData,
        loadingStatus: this.loadingStatus,
        isHasButtons: false,
        isShowNumber: true,
        isShowPagination: true,
        rowClassName: this.getRowClassName,
      };
    },
    tableEvent() {
      return {
        subClickPagination: this.handlePagination,
      };
    },
  },
  created() {
    this.loadBalanceStats();
  },
  methods: {
    getRowClassName({ row }) {
      if (row.isNegativeRemain) {
        return "merchant-pay-negative-row";
      }
      return row.isSummary ? "merchant-pay-summary-row" : "";
    },
    isNegativeRemain(value) {
      const n = parseFloat(String(value == null ? "" : value).replace(/,/g, ""));
      return !Number.isNaN(n) && n < 0;
    },
    handlePagination() {
      // 前端分页，数据已在 tableData
    },
    async fetchMerchantSummary(merchant) {
      const { data } = await platformMerchantRechargeList({
        page: 0,
        page_size: 1,
        merchantId: merchant.merchantId,
      });
      const list = (data && data.list) || [];
      const summary =
        list.find((item) => item.remark === SUMMARY_REMARK) || list[0];
      if (!summary) {
        return null;
      }
      return {
        merchantId: merchant.merchantId,
        merchantName: merchant.merchantName,
        pay_amount: summary.pay_amount,
        total_amount: summary.total_amount,
        total_merchant_amount: summary.total_merchant_amount,
        remain_amount: summary.remain_amount,
        isNegativeRemain: this.isNegativeRemain(summary.remain_amount),
        remark: summary.remark,
        create_time: summary.create_time,
        isSummary: true,
      };
    },
    async loadBalanceStats() {
      try {
        this.loadingStatus = true;
        this.merchantOptions = await fetchActiveMerchants();
        const results = await Promise.all(
          this.merchantOptions.map((merchant) =>
            this.fetchMerchantSummary(merchant).catch(() => null)
          )
        );
        this.tableData = results
          .filter(Boolean)
          .sort((a, b) => {
            if (a.isNegativeRemain !== b.isNegativeRemain) {
              return a.isNegativeRemain ? -1 : 1;
            }
            return String(a.merchantName).localeCompare(
              String(b.merchantName),
              "zh-CN"
            );
          });
        this.listQueryParams.pageNum = 1;
        this.listQueryParams.total = this.tableData.length;
      } catch (error) {
        console.log(error);
        this.tableData = [];
        this.listQueryParams.total = 0;
        this.$message.error("加载余额统计失败");
      } finally {
        this.loadingStatus = false;
      }
    },
    exportExcel() {
      if (!this.tableData.length) {
        this.$message.warning("暂无数据可导出");
        return;
      }
      const exportData = this.tableData.map((item) =>
        EXPORT_KEYS.map((key) => item[key])
      );
      exportData.unshift(EXPORT_HEADERS);
      const ws = XLSX.utils.aoa_to_sheet(exportData);
      ws["!cols"] = [
        { wch: 24 },
        { wch: 12 },
        { wch: 14 },
        { wch: 14 },
        { wch: 12 },
        { wch: 21 },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      XLSX.writeFile(wb, `平台商户余额统计_${getPathName()}.xlsx`);
    },
  },
};
</script>

<style lang="scss" scoped>
.merchant-pay {
  padding: 20px;

  &__toolbar {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
  }

  &__alert {
    margin-bottom: 12px;
  }

  ::v-deep .merchant-pay-summary-row {
    background-color: #e6f7ff !important;

    td {
      color: #096dd9;
      font-weight: 600;
    }
  }

  ::v-deep .merchant-pay-negative-row {
    background-color: #fff1f0 !important;

    td {
      color: #a8071a !important;
      font-weight: 600;
    }

    &:hover > td {
      background-color: #ffccc7 !important;
    }
  }
}
</style>
